/**
 * Nederlandse teksten voor het tabblad Overzicht van een keuring (scherm 8,
 * vroeger Dossier): alles van één keuring op één scherm. Wat de keuring is en de
 * tabbladen staan in de kop eromheen (`app/layouts/copy.ts`).
 */
export const dossierCopy = {
    loading: 'Overzicht laden',

    lifecycleTitle: 'Verloop van de keuring',
    lifecycleNote: 'Elke stap gaat automatisch, tenzij je ingrijpt.',
    cancelled: (reason: string | null) =>
        reason ? `Geannuleerd: ${reason}` : 'Deze keuring is geannuleerd.',

    buildYear: 'Bouwjaar',
    propertyType: 'Type',
    floorArea: 'Oppervlak',
    inspection: 'Opname',
    unknown: '—',
    notPlanned: 'Nog niet ingepland',
    inspector: (name: string) => `Inspecteur: ${name}`,

    timelineTitle: 'Tijdlijn',
    timelineEmpty: 'Nog geen gebeurtenissen.',
    timelineAutomatic: 'Automatisch',
    timelineUnknownActor: 'Onbekend',

    documentsTitle: 'Documenten',
    documentsEmpty: 'Nog geen documenten. Rapporten en facturen verschijnen hier zodra ze er zijn.',
    documentsLoading: 'Documenten laden',
    documentsError: 'De documenten konden niet geladen worden',
    documentOpen: (name: string) => `${name} openen`,
    documentKinds: {
        report: 'Rapport',
        invoice: 'Factuur',
        attachment: 'Bijlage',
        other: 'Document',
    },

    services: {
        title: 'Diensten',
        notes: {
            survey: 'Opname met de vragenlijst, ter plaatse.',
            report: 'Komt in het rapport van deze keuring, ook als de NHG-bijlage onder Instellingen → Rapport uitstaat.',
            manual: 'Gemaakt buiten Smartkeur.',
            manualOpen: 'Maak je buiten Smartkeur. Meld hem hier klaar zodra hij er is.',
        },
        surveyNotPlanned: 'Nog niet ingepland',
        surveyNotReady: 'Nog niet klaargezet',
        inReport: 'In het rapport',
        open: 'Nog open',
        done: 'Klaar',
        cancelled: 'Geannuleerd',
        complete: 'Klaar melden',
        confirmQuestion: (service: string) =>
            `${service} klaar melden? Doe dat pas als hij er is: terugzetten kan niet.`,
        confirm: 'Ja, klaar melden',
        keep: 'Niet doen',
        busy: 'Bezig met klaar melden',
        completed: (service: string) => `${service} is klaar gemeld.`,
        failed: 'Klaar melden lukte niet. Probeer het opnieuw.',
    },

    clientTitle: 'Klant',
    notesTitle: 'Opmerking bij de keuring',

    editClient: {
        button: 'Klant bewerken',
        title: 'Klant bewerken',
        description:
            'Wat je hier aanpast, geldt voor alle keuringen van deze klant, en voor een rapport dat nog niet verstuurd is.',
        close: 'Sluiten',
        name: 'Naam',
        nameMissing: 'Vul de naam van de klant in.',
        contactName: 'Contactpersoon',
        contactNameHint: 'Zo spreken de mail en de klantpagina de klant aan. Leeg: de naam.',
        email: 'E-mailadres',
        emailHint: 'Hier gaan het rapport en de berichten over de afspraak naartoe.',
        phone: 'Telefoon',
        phoneHint: 'Met een telefoonnummer krijgt de klant de afspraak en het rapport ook per sms.',
        addressSection: 'Adres van de klant',
        addressHint: 'Het adres van de klant zelf, niet dat van de woning die gekeurd wordt.',
        addressIncomplete: 'Maak het adres af, of maak de velden leeg.',
        save: 'Klant opslaan',
        saving: 'Bezig met opslaan…',
        cancel: 'Annuleren',
        saved: (name: string) => `De gegevens van ${name} zijn opgeslagen.`,
    },

    changeAddress: {
        button: 'Adres van het object wijzigen',
        title: 'Adres van het object wijzigen',
        description:
            'Alleen voor deze keuring: andere keuringen op het oude adres blijven staan. Een rapport dat nog niet verstuurd is, krijgt het nieuwe adres. De opname blijft zoals hij is.',
        close: 'Sluiten',
        current: (address: string) => `Nu: ${address}`,
        incomplete: 'Vul postcode en huisnummer in en kies het juiste adres.',
        locked: 'Het rapport is al verstuurd; neem contact op met Smartkeur.',
        save: 'Adres wijzigen',
        saving: 'Bezig met wijzigen…',
        cancel: 'Annuleren',
        saved: (address: string) => `De keuring staat nu op ${address}.`,
    },

    financeTitle: 'Financieel',
    financeFee: 'Vergoeding inspecteur',
    financeMargin: 'Marge',
    financeCredits: (reserved: number | null, consumed: number | null) =>
        `Tegoed: ${reserved ?? 0} gereserveerd · ${consumed ?? 0} verbruikt`,

    /*
     * Wat "prijs aanpassen" wel en niet doet. Dit staat hier, op de plek waar de
     * prijs van de keuring staat, en niet in een handleiding: het zijn precies de
     * vragen die een keurmeester stelt zodra hij een bedrag ziet dat hij kan
     * wijzigen.
     */
    financeAboutPrice: 'Over deze prijs',
    financeOnlyThisOrder:
        'Deze prijs hoort bij deze keuring en bij niets anders. Hij is vastgelegd toen de keuring werd aangenomen en verandert niet meer als je later je tarieven wijzigt.',
    financeTariffElsewhere:
        'Je standaardtarief verander je bij Facturatie & tarieven. Dat gaat in op de datum die je daar kiest en geldt alleen voor keuringen die je daarna aanneemt - nooit met terugwerkende kracht.',
    financeNoAutoPrice:
        'De prijs rekent niet mee met m², het aantal kozijnen of andere aantallen die je invult. Bedragen die dat wél doen zijn de herstelkosten bij een bevinding: die gaan naar het rapport voor de koper, niet naar deze factuur.',
    financeInvoiceEditable:
        'Op de conceptfactuur kun je het bedrag, de btw en extra kosten nog wijzigen tot je hem verstuurt.',

    financeToBilling: 'Naar facturatie',

    errorTitle: 'De keuring kon niet geladen worden',
    timelineError: 'De tijdlijn kon niet geladen worden',
} as const;
